import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Homebase from './Homebase';
import Personnel from './Personnel';
import Team from './Team';
import Rider from './Rider';
import VehicleModel from './VehicleModel';
import Vehicle from './Vehicle';
import Devices from './Devices';
import PartnerHomebase from './PartnerHomebase';

/* ── Entity tab definitions ─────────────────────────────────────── */
const ALL_TABS = [
  { key: 'vehicle-model', label: 'Vehicle Model',    icon: 'fa-car',       color: '#3b82f6', bg: '#dbeafe', superOnly: false },
  { key: 'vehicle',       label: 'Vehicle',          icon: 'fa-truck',     color: '#8b5cf6', bg: '#ede9fe', superOnly: false },
  { key: 'rider',         label: 'Rider',            icon: 'fa-user',      color: '#06b6d4', bg: '#cffafe', superOnly: false },
  { key: 'team',          label: 'Team',             icon: 'fa-users',     color: '#f59e0b', bg: '#fef3c7', superOnly: true  },
  { key: 'devices',       label: 'Devices',          icon: 'fa-mobile',    color: '#ef4444', bg: '#fee2e2', superOnly: true  },
  { key: 'personnel',     label: 'Personnel',        icon: 'fa-id-badge',  color: '#10b981', bg: '#d1fae5', superOnly: true  },
  { key: 'homebase',      label: 'Homebase',         icon: 'fa-home',      color: '#f97316', bg: '#ffedd5', superOnly: true  },
  { key: 'partner-warehouse', label: 'Warehouses',   icon: 'fa-building',  color: '#6366f1', bg: '#eef2ff', superOnly: true  },
];

export default function EntityManagement() {
  const seg = useParams();
  /* When rendered from Sidebar.js the wildcard may be empty; fall back to pathname */
  const location = useLocation();
  const segment = seg['*'] || location.pathname.split('/').pop();
  const navigate = useNavigate();

  const role = sessionStorage.getItem('role') || '';
  const isSuperAdmin = role === 'super_admin';
  const isAdmin = isSuperAdmin || role === 'admin';
  const tabs = ALL_TABS.filter(t => !t.superOnly || isSuperAdmin);

  const active = ALL_TABS.find(t => t.key === segment) || tabs[0];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc', fontFamily: "'Poppins', sans-serif" }}>
      <style>{`
        @keyframes fadeUp   { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeIn   { from{opacity:0} to{opacity:1} }
        .em-tab-btn { border:none; cursor:pointer; border-radius:14px; padding:9px 16px; font-family:'Poppins',sans-serif; font-weight:600; font-size:12px; transition:all 0.18s; display:inline-flex; align-items:center; gap:7px; white-space:nowrap; text-decoration:none; }
        .em-tab-btn:hover { opacity:0.85; transform:translateY(-1px); }
        .em-content-wrap { animation: fadeIn 0.35s ease-out both; }
      `}</style>

      {/* ── Page Header ────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)',
        padding: '22px 28px 18px', margin: '16px 16px 0', borderRadius: 20,
        boxShadow: '0 8px 32px rgba(15,23,42,0.22)',
        animation: 'fadeUp 0.4s ease-out both',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className="fa fa-database" style={{ fontSize: 20, color: '#fff' }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#fff' }}>Entity Management</h1>
              <p style={{ margin: '2px 0 0', opacity: 0.55, fontSize: 12 }}>Manage vehicles, riders, teams, bases & more</p>
            </div>
          </div>
          {active && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 12, padding: '8px 16px' }}>
              <i className={`fa ${active.icon}`} style={{ color: '#fff', fontSize: 14 }} />
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>{active.label}</span>
            </div>
          )}
        </div>
      </div>

      {/* ── Entity Tabs ─────────────────────────────────────────────── */}
      <div style={{ padding: '14px 16px 0', display: 'flex', gap: 8, flexWrap: 'wrap', animation: 'fadeUp 0.4s 0.07s ease-out both' }}>
        {tabs.map(tab => {
          const isActive = segment === tab.key;
          return (
            <a
              key={tab.key}
              href={`/entity/${tab.key}`}
              className="em-tab-btn"
              style={{
                background: isActive ? tab.color : '#fff',
                color: isActive ? '#fff' : tab.color,
                border: `2px solid ${tab.color}`,
                boxShadow: isActive ? `0 4px 14px ${tab.color}40` : '0 1px 4px rgba(0,0,0,0.06)',
              }}
            >
              <i className={`fa ${tab.icon}`} style={{ fontSize: 12 }} />
              {tab.label}
            </a>
          );
        })}
      </div>

      {/* ── Content ─────────────────────────────────────────────────── */}
      <div className="em-content-wrap" style={{ margin: '16px 16px 20px', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px -4px rgba(0,0,0,0.08)', minHeight: 400 }}>
        {/* Entity sub-header strip */}
        {active && (
          <div style={{
            background: `linear-gradient(90deg, ${active.color}18, ${active.bg})`,
            borderBottom: `2px solid ${active.color}22`,
            borderRadius: '16px 16px 0 0',
            padding: '14px 24px',
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: active.bg, border: `1.5px solid ${active.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <i className={`fa ${active.icon}`} style={{ color: active.color, fontSize: 14 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{active.label}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>Entity · {active.label} management</div>
            </div>
          </div>
        )}

        {/* Render entity component */}
        <div style={{ padding: '4px 0' }}>
          {segment === 'homebase'          && <Homebase />}
          {segment === 'personnel'         && <Personnel />}
          {segment === 'team'              && <Team />}
          {segment === 'rider'             && <Rider />}
          {segment === 'vehicle-model'     && <VehicleModel />}
          {segment === 'vehicle'           && <Vehicle />}
          {segment === 'devices'           && <Devices />}
          {segment === 'partner-warehouse' && <PartnerHomebase />}

          {/* Default: no match */}
          {!ALL_TABS.some(t => t.key === segment) && (
            <div style={{ textAlign: 'center', padding: '60px 24px', color: '#94a3b8' }}>
              <i className="fa fa-database" style={{ fontSize: 40, opacity: 0.25, display: 'block', marginBottom: 12 }} />
              <div style={{ fontWeight: 700, fontSize: 16, color: '#334155', marginBottom: 6 }}>Select an entity</div>
              <div style={{ fontSize: 13 }}>Choose a category from the tabs above to get started.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
